import { round2 } from './money.js';
import { isWorkingDay } from './working-days.js';

/**
 * Utilization: billable hours divided by the hours the person was available (F5 in
 * docs/dashboard/1-1-Storskaerm-Dashboard-Designnotat.md).
 *
 *   working days  = weekdays in the period - holidays
 *   daily norm    = weekly hours / 5
 *   available     = working days x daily norm - absence days x daily norm
 *   utilization   = billable hours / available
 *
 * The example from the design: September 2026 has 22 working days, and 37 hours a week gives
 * 162.8 hours. Three days of vacation in the absence calendar is 22.2 hours, so 140.6 hours are
 * available, and 98 billable hours is 70 %.
 *
 * **Absence is in days.** The absence calendar in Outlook has all-day events and events with
 * times; both become days per date - see `absenceByDate` - and are converted back with the same
 * daily norm as the working days. Days off, school and courses from the calendar on
 * /admin/tv-dashboard are whole days - see `calendarDaysOff` - and count the same way.
 *
 * Pure functions, so the wall screen and the preview on /admin/tv-dashboard compute the same.
 */

export const WORK_DAYS_PER_WEEK = 5;

export function dailyNormHours(weeklyHours: number): number {
  return weeklyHours / WORK_DAYS_PER_WEEK;
}

/**
 * Timerne til raadighed. Aldrig under nul: mere fravaer end arbejdsdage kan kun ske, hvis en
 * registrering er forkert, og en negativ naevner ville give en negativ grad.
 */
export function availableHours(
  workingDays: number,
  absenceDays: number,
  weeklyHours: number,
): number {
  const days = Math.max(0, workingDays - Math.max(0, absenceDays));
  return round2(days * dailyNormHours(Math.max(0, weeklyHours)));
}

/** Graden i procent med én decimal. Null, naar der ingen timer var til raadighed. */
export function utilizationPct(billableHours: number, available: number): number | null {
  if (!Number.isFinite(billableHours) || !Number.isFinite(available) || available <= 0) {
    return null;
  }
  return Math.round((billableHours / available) * 1000) / 10;
}

/**
 * One event in the absence calendar, as Outlook has it: Danish local time without an offset, e.g.
 * `2026-10-05T00:00:00.0000000`. An all-day event ends at midnight after its last day.
 */
export interface AbsenceSpan {
  start: string;
  end: string;
  all_day: boolean;
}

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * A local time as milliseconds, computed as if it were UTC. Only dates and differences are read
 * from it, so the missing offset does not matter. NaN when the value is not a date.
 */
function localMs(value: string): number {
  const [date = '', time = ''] = value.split('T');
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes, seconds] = time.slice(0, 8).split(':').map(Number);
  return Date.UTC(
    year ?? Number.NaN,
    (month ?? Number.NaN) - 1,
    day ?? Number.NaN,
    hours || 0,
    minutes || 0,
    seconds || 0,
  );
}

function isoDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Absence per Danish date, in days from 0 to 1, for one person.
 *
 * **An all-day event** counts every working day it covers as a whole day. Its end is exclusive:
 * a vacation from Monday to Friday ends on Saturday at midnight.
 *
 * **An event with times** counts its hours on each date, converted with the daily norm and capped
 * at a whole day. A visit to the dentist from 9 to 12 is 3 / 7.4 = 0.41 days at 37 hours a week.
 *
 * Weekends and holidays count nothing - they are not among the available hours either - and
 * overlapping events never make more than one day on a date.
 */
export function absenceByDate(
  spans: readonly AbsenceSpan[],
  weeklyHours: number,
): Map<string, number> {
  const dailyMs = dailyNormHours(Math.max(0, weeklyHours)) * HOUR_MS;
  const result = new Map<string, number>();
  const add = (dayMs: number, days: number): void => {
    const date = isoDate(dayMs);
    if (days <= 0 || !isWorkingDay(date)) return;
    result.set(date, Math.min(1, (result.get(date) ?? 0) + days));
  };

  for (const span of spans) {
    const start = localMs(span.start);
    const end = localMs(span.end);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
    const firstDay = start - (start % DAY_MS);

    if (span.all_day) {
      // At least the first day, even when the end is before the start.
      const last = Math.max(end, firstDay + DAY_MS);
      for (let day = firstDay; day < last; day += DAY_MS) add(day, 1);
      continue;
    }

    for (let day = firstDay; day < end; day += DAY_MS) {
      const overlap = Math.min(end, day + DAY_MS) - Math.max(start, day);
      add(day, dailyMs > 0 ? overlap / dailyMs : 1);
    }
  }

  return result;
}

/** The part of a calendar entry that decides its days - see `TvCalendarEntry`. */
export interface CalendarRange {
  start_date: string;
  end_date: string;
  /** ISO weekdays, Monday = 1. Empty = every working day. */
  weekdays: readonly number[];
}

/**
 * The working days a calendar entry takes, in order: every working day from its first day to its
 * last, and only on its weekdays when it names any. Weekends and holidays are not working days to
 * begin with, so a week of school is five days whether or not the range runs over the weekend.
 */
export function calendarEntryDates(entry: CalendarRange): string[] {
  const first = localMs(entry.start_date);
  const last = localMs(entry.end_date);
  if (!Number.isFinite(first) || !Number.isFinite(last)) return [];

  const dates: string[] = [];
  for (let day = first; day <= last; day += DAY_MS) {
    const date = isoDate(day);
    if (!isWorkingDay(date)) continue;
    // getUTCDay is 1-5 on a working day, the same numbering as ISO's Monday to Friday.
    if (entry.weekdays.length > 0 && !entry.weekdays.includes(new Date(day).getUTCDay())) continue;
    dates.push(date);
  }
  return dates;
}

/**
 * One person's days off in the calendar, one whole day per date - the same shape as
 * `absenceByDate`, so `absenceDaysBetween` counts both. An entry without people is for everyone.
 */
export function calendarDaysOff(
  entries: ReadonlyArray<CalendarRange & { user_ids: readonly string[] | null }>,
  userId: string,
): Map<string, number> {
  const result = new Map<string, number>();
  for (const entry of entries) {
    if (entry.user_ids !== null && !entry.user_ids.includes(userId)) continue;
    for (const date of calendarEntryDates(entry)) result.set(date, 1);
  }
  return result;
}

/** Absence on the dates that are not already off: a sick day at school costs no extra hours. */
export function absenceOutside(
  absence: ReadonlyMap<string, number>,
  off: ReadonlyMap<string, number>,
): Map<string, number> {
  return new Map([...absence].filter(([date]) => !off.has(date)));
}

/** The absence days from `start` up to and including `end` - see `absenceByDate`. */
export function absenceDaysBetween(
  byDate: ReadonlyMap<string, number>,
  start: string,
  end: string,
): number {
  let days = 0;
  for (const [date, value] of byDate) {
    if (date >= start && date <= end) days += value;
  }
  return round2(days);
}

/**
 * Husets maal, naar personerne har hver sit: maalene vaegtet med de timer, hver person var til
 * raadighed.
 *
 * Det er det tal, der gaar op: rammer hver person sit eget maal, rammer huset sit. Har ingen et
 * personligt maal, er svaret standardmaalet. Uden timer til raadighed er det ogsaa standarden -
 * der er intet at vaegte med.
 */
export function weightedTargetPct(
  people: ReadonlyArray<{ available_hours: number; target_pct: number }>,
  fallback: number,
): number {
  const total = people.reduce((sum, person) => sum + Math.max(0, person.available_hours), 0);
  if (total <= 0) return fallback;

  const weighted = people.reduce(
    (sum, person) => sum + person.target_pct * Math.max(0, person.available_hours),
    0,
  );
  return Math.round((weighted / total) * 10) / 10;
}
